import type { CdkVirtualScrollViewport, VirtualScrollStrategy } from '@angular/cdk/scrolling'
import { Subject, distinctUntilChanged } from 'rxjs'

const BUFFER_ITEMS = 5

/**
 * Calculates item offsets and total content height.
 * @param {readonly number[]} itemHeights - Positive fixed heights in render order.
 * @returns {number[]} Item offsets followed by the total content height.
 */
export function getItemOffsets(itemHeights: readonly number[]): number[] {
  const offsets = [0]

  for (const height of itemHeights) {
    if (!Number.isFinite(height) || height <= 0) {
      throw new Error('Virtual scroll item heights must be finite and greater than zero.')
    }

    offsets.push(offsets[offsets.length - 1] + height)
  }

  return offsets
}

/** Virtualizes items with known, different heights. Heights must match the rendered rows. */
export class CustomVirtualScrollStrategyDirective implements VirtualScrollStrategy {
  private viewport?: CdkVirtualScrollViewport
  private readonly scrolledIndexChange$ = new Subject<number>()
  private itemOffsets: readonly number[]

  readonly scrolledIndexChange = this.scrolledIndexChange$.pipe(distinctUntilChanged())

  /**
   * Initializes fixed item heights.
   * @param {readonly number[]} itemHeights
   */
  constructor(itemHeights: readonly number[]) {
    this.itemOffsets = getItemOffsets(itemHeights)
  }

  /**
   * Sets the viewport's content size and rendered range.
   * @param {CdkVirtualScrollViewport} viewport
   */
  attach(viewport: CdkVirtualScrollViewport): void {
    this.viewport = viewport
    this.updateTotalContentSize()
    this.updateRenderedRange()
  }

  /**
   * Releases the viewport and ends index notifications.
   */
  detach(): void {
    this.scrolledIndexChange$.complete()
    this.viewport = undefined
  }

  /**
   * Updates scrolling after item heights or order change.
   * @param {readonly number[]} itemHeights
   */
  updateItemHeights(itemHeights: readonly number[]): void {
    this.itemOffsets = getItemOffsets(itemHeights)
    this.updateTotalContentSize()
    this.updateRenderedRange()
  }

  /**
   * Updates the visible and buffered rows after scrolling.
   */
  onContentScrolled(): void {
    this.updateRenderedRange()
  }

  /**
   * Updates scrolling after data or viewport size changes.
   */
  onDataLengthChanged(): void {
    this.updateTotalContentSize()
    this.updateRenderedRange()
  }

  /**
   * Fixed heights need no measurements after rendering.
   */
  onContentRendered(): void {}

  /**
   * Fixed heights need no measurements after offset changes.
   */
  onRenderedOffsetChanged(): void {}

  /**
   * Scrolls to an item.
   * @param {number} index - Item index, clamped to the available offsets.
   * @param {ScrollBehavior} behavior
   */
  scrollToIndex(index: number, behavior: ScrollBehavior): void {
    const offsetIndex = Math.max(0, Math.min(Math.floor(index), this.itemOffsets.length - 1))
    this.viewport?.scrollToOffset(this.itemOffsets[offsetIndex], behavior)
  }

  /**
   * Finds the item containing a scroll offset.
   * @param {number} offset - Scroll offset in pixels.
   * @returns {number} Item index, clamped to the last item.
   */
  private findItemIndex(offset: number): number {
    let start = 0
    let end = this.itemOffsets.length - 1

    while (start < end) {
      const middle = Math.floor((start + end) / 2)

      if (this.itemOffsets[middle] <= offset) {
        start = middle + 1
      } else {
        end = middle
      }
    }

    return Math.max(0, Math.min(start - 1, this.itemOffsets.length - 2))
  }

  /**
   * Updates the buffered rows and their position.
   */
  private updateRenderedRange(): void {
    const viewport = this.viewport

    if (!viewport) {
      return
    }

    const itemCount = this.itemOffsets.length - 1

    if (!itemCount) {
      viewport.setRenderedRange({ start: 0, end: 0 })
      viewport.setRenderedContentOffset(0)
      this.scrolledIndexChange$.next(0)

      return
    }

    const viewportSize = viewport.getViewportSize()
    const maximumOffset = Math.max(0, this.itemOffsets[itemCount] - viewportSize)
    // Content can shrink before the browser adjusts its native scroll offset.
    const scrollOffset = Math.max(0, Math.min(viewport.measureScrollOffset(), maximumOffset))
    const firstVisibleIndex = this.findItemIndex(scrollOffset)
    const visibleEnd = this.findItemIndex(scrollOffset + viewportSize) + 1
    const range = {
      start: Math.max(0, firstVisibleIndex - BUFFER_ITEMS),
      end: Math.min(itemCount, visibleEnd + BUFFER_ITEMS)
    }

    viewport.setRenderedRange(range)
    viewport.setRenderedContentOffset(this.itemOffsets[range.start])
    this.scrolledIndexChange$.next(firstVisibleIndex)
  }

  /**
   * Sets the full scrollable content height.
   */
  private updateTotalContentSize(): void {
    this.viewport?.setTotalContentSize(this.itemOffsets[this.itemOffsets.length - 1])
  }
}
